import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Loader2, Save, TrendingUp, Briefcase, Target, MapPin, Tag, FileText, BarChart3
} from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function RepProfilePanel({ open, onOpenChange, userId, userName }) {
    const { getAuthHeader } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState(null);
    const [notes, setNotes] = useState('');
    const [territories, setTerritories] = useState('');
    const [specialties, setSpecialties] = useState('');
    const [maxAccounts, setMaxAccounts] = useState('');

    useEffect(() => {
        if (!open || !userId) return;
        const load = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API}/api/admin/users/${userId}/rep-profile`, { headers: getAuthHeader() });
                setData(res.data);
                setNotes(res.data.profile?.admin_notes_md || '');
                setTerritories((res.data.profile?.territories || []).join(', '));
                setSpecialties((res.data.profile?.specialties || []).join(', '));
                setMaxAccounts(res.data.profile?.max_accounts || '');
            } catch { toast.error('Failed to load rep profile'); }
            finally { setLoading(false); }
        };
        load();
    }, [open, userId, getAuthHeader]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/api/admin/users/${userId}/rep-profile`, {
                admin_notes_md: notes,
                territories: territories.split(',').map(t => t.trim()).filter(Boolean),
                specialties: specialties.split(',').map(s => s.trim()).filter(Boolean),
                max_accounts: maxAccounts ? parseInt(maxAccounts) : null,
            }, { headers: getAuthHeader() });
            toast.success('Rep profile saved');
        } catch { toast.error('Failed to save'); }
        finally { setSaving(false); }
    };

    const perf = data?.performance || {};
    const workload = data?.workload || {};

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-stone-700" />
                        Rep Profile — {userName || 'Sales Rep'}
                    </DialogTitle>
                    <DialogDescription>Private admin notes and performance data. Not visible to the sales rep.</DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-stone-400 animate-spin" /></div>
                ) : (
                    <div className="space-y-5">
                        {/* Performance Stats */}
                        <div>
                            <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                <BarChart3 className="w-3.5 h-3.5" /> Performance
                            </h4>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                <div className="bg-stone-50 rounded-lg p-3 text-center">
                                    <div className="text-xl font-bold text-stone-900">{perf.win_rate || 0}%</div>
                                    <div className="text-xs text-stone-500">Win Rate</div>
                                </div>
                                <div className="bg-stone-50 rounded-lg p-3 text-center">
                                    <div className="text-xl font-bold text-stone-900">{perf.won_deals || 0}</div>
                                    <div className="text-xs text-stone-500">Deals Won</div>
                                </div>
                                <div className="bg-stone-50 rounded-lg p-3 text-center">
                                    <div className="text-xl font-bold text-stone-900">${(perf.avg_deal_value || 0).toLocaleString()}</div>
                                    <div className="text-xs text-stone-500">Avg Deal Value</div>
                                </div>
                                <div className="bg-stone-50 rounded-lg p-3 text-center">
                                    <div className="text-xl font-bold text-stone-900">${(perf.total_won_value || 0).toLocaleString()}</div>
                                    <div className="text-xs text-stone-500">Total Won</div>
                                </div>
                            </div>
                        </div>

                        {/* Industry Breakdown */}
                        {perf.deals_by_industry && Object.keys(perf.deals_by_industry).length > 0 && (
                            <div>
                                <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Deals by Industry</h4>
                                <div className="space-y-1">
                                    {Object.entries(perf.deals_by_industry).slice(0, 6).map(([industry, stats]) => {
                                        const total = stats.won + stats.lost + stats.open;
                                        const rate = total > 0 ? Math.round((stats.won / Math.max(stats.won + stats.lost, 1)) * 100) : 0;
                                        return (
                                            <div key={industry} className="flex items-center gap-3 py-1.5 px-3 rounded-lg bg-stone-50 text-sm">
                                                <span className="font-medium text-stone-700 w-32 truncate">{industry}</span>
                                                <span className="text-emerald-600 text-xs">{stats.won}W</span>
                                                <span className="text-red-500 text-xs">{stats.lost}L</span>
                                                <span className="text-stone-400 text-xs">{stats.open} open</span>
                                                <span className={`ml-auto text-xs font-semibold px-1.5 py-0.5 rounded ${rate >= 60 ? 'bg-emerald-100 text-emerald-700' : rate >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                                                    {rate}% WR
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Current Workload */}
                        <div>
                            <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Current Workload</h4>
                            <div className="grid grid-cols-3 gap-2">
                                <div className="bg-blue-50 rounded-lg p-2.5 text-center">
                                    <div className="text-lg font-bold text-blue-700">{workload.current_pipeline_count || 0}</div>
                                    <div className="text-xs text-blue-600">Open Deals</div>
                                </div>
                                <div className="bg-amber-50 rounded-lg p-2.5 text-center">
                                    <div className="text-lg font-bold text-amber-700">{workload.current_accounts_count || 0}</div>
                                    <div className="text-xs text-amber-600">Companies</div>
                                </div>
                                <div className="bg-purple-50 rounded-lg p-2.5 text-center">
                                    <div className="text-lg font-bold text-purple-700">{workload.current_targets_count || 0}</div>
                                    <div className="text-xs text-purple-600">Targets Assigned</div>
                                </div>
                            </div>
                        </div>

                        {/* Admin Notes & Config */}
                        <div className="border-t border-stone-200 pt-4 space-y-4">
                            <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider flex items-center gap-1.5">
                                <FileText className="w-3.5 h-3.5" /> Admin Notes (Private)
                            </h4>

                            <div>
                                <label className="block text-xs font-medium text-stone-600 mb-1">Notes (Markdown supported)</label>
                                <textarea
                                    value={notes}
                                    onChange={e => setNotes(e.target.value)}
                                    rows={5}
                                    placeholder="e.g. Strong in healthcare SaaS. Prefers inbound leads. Closing rate drops on deals >$500K. Great with technical buyers."
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500 font-mono"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-stone-600 mb-1">
                                        <MapPin className="w-3 h-3 inline mr-1" />Territories (comma-separated)
                                    </label>
                                    <input
                                        value={territories}
                                        onChange={e => setTerritories(e.target.value)}
                                        placeholder="North America, APAC"
                                        className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-stone-600 mb-1">
                                        <Tag className="w-3 h-3 inline mr-1" />Specialties (comma-separated)
                                    </label>
                                    <input
                                        value={specialties}
                                        onChange={e => setSpecialties(e.target.value)}
                                        placeholder="Healthcare, FinTech, Enterprise"
                                        className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                    />
                                </div>
                            </div>

                            <div className="w-1/2">
                                <label className="block text-xs font-medium text-stone-600 mb-1">
                                    <Target className="w-3 h-3 inline mr-1" />Max Accounts Capacity
                                </label>
                                <input
                                    type="number"
                                    value={maxAccounts}
                                    onChange={e => setMaxAccounts(e.target.value)}
                                    placeholder="No limit"
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end pt-2 border-t border-stone-100">
                            <Button onClick={handleSave} disabled={saving}>
                                {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving...</> :
                                <><Save className="w-4 h-4 mr-1.5" /> Save Profile</>}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
